import React from "react";
import { SoccerResult } from "./SoccerGameRun.tsx";
import { countOf, goalsOf, KICKS_PER_MATCH } from "./soccerGame.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";
import { format } from "../../Shared/i18n/format.ts";

interface SoccerGameSummaryProps {
    result: SoccerResult;
    /** Best score for these facts and this keeper, after this match. */
    best: number | null;
    isNewBest: boolean;
    onPlayAgain(): void;
    onChangeKeeper(): void;
}

/**
 * Full time: goals out of ten, then two lists. Facts that went wide or were
 * skipped are the ones worth another look. Facts that were saved were *right*,
 * only slow, and are listed apart - "you know this, now get it quicker" is the
 * most useful thing the game can say (ADR 0059 §1).
 */
export function SoccerGameSummary({ result, best, isNewBest, onPlayAgain, onChangeKeeper }: SoccerGameSummaryProps) {
    const t = useTranslation();
    const { kicks } = result;
    const misses = kicks.filter((k) => k.outcome === "wide" || k.outcome === "skipped");
    const slow = kicks.filter((k) => k.outcome === "saved");

    return (
        <div className="quizSummary soccerSummary">
            <p className="quizSummaryCounts">{t("flashCards.soccerFullTime")}</p>
            <p className="quizSummaryScore">{goalsOf(kicks)}</p>
            <p className="quizSummaryScoreLabel">
                {format(t("flashCards.soccerGoalsFromTemplate"), { total: KICKS_PER_MATCH })}
            </p>
            <p className="quizSummaryCounts">
                {format(t("flashCards.soccerCountsTemplate"), {
                    saved: countOf(kicks, "saved"),
                    wide: countOf(kicks, "wide"),
                    skipped: countOf(kicks, "skipped"),
                })}
            </p>

            {best !== null && (
                <p className={`quizSummaryBest${isNewBest ? " quizSummaryBest--new" : ""}`}>
                    {isNewBest ? t("flashCards.newBest") : format(t("flashCards.yourBestTemplate"), { n: best })}
                </p>
            )}

            {slow.length > 0 && (
                <div className="quizSummaryMisses soccerSummarySlow">
                    <h3>{t("flashCards.soccerSlowHeading")}</h3>
                    <ul>
                        {slow.map((k) => (
                            <li key={k.number}>
                                <span className="quizSummaryMissProblem">
                                    {k.front} = {k.answer}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {misses.length > 0 && (
                <div className="quizSummaryMisses">
                    <h3>{t("flashCards.worthAnotherLookHeading")}</h3>
                    <ul>
                        {misses.map((k) => (
                            <li key={k.number}>
                                <span className="quizSummaryMissProblem">
                                    {k.front} = {k.answer}
                                </span>
                                <span className="quizSummaryMissYours">
                                    {k.outcome === "skipped"
                                        ? t("flashCards.skippedLower")
                                        : format(t("flashCards.youPutTemplate"), { typed: k.typed })}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <button type="button" className="quizPlayAgain" onClick={onPlayAgain}>
                {t("flashCards.playAgain")}
            </button>
            <button type="button" className="quizChangeMode" onClick={onChangeKeeper}>
                {t("flashCards.soccerChangeKeeper")}
            </button>
        </div>
    );
}
